import { useState } from "react";
import { Navigate, NavLink, Outlet, Link } from "react-router-dom";
import {
  ShoppingCart, Workflow, Disc3, FileSpreadsheet, Upload, Trash2, Smile, LayoutGrid, Menu, Truck, FileText, PenSquare, LogOut, ExternalLink, X, Mail, Ticket, Speaker, Image,
} from "lucide-react";
import { TOKEN_KEY } from "@/lib/api";
import { useLogo } from "@/lib/store";

const NAV = [
  ["Sales", [["orders", "Orders", ShoppingCart], ["orders-import", "Import old orders", Upload], ["stages", "Order flow", Workflow], ["emails", "Customer emails", Mail], ["coupons", "Coupons", Ticket]]],
  ["Catalogue", [["products", "Products", Disc3], ["stock", "Stock & price sheet", FileSpreadsheet], ["import", "Upload products", Upload], ["delete", "Bulk delete", Trash2], ["moods", "Shop by mood", Smile]]],
  ["Turntables & Accessories", [["gear-stock", "Gear stock sheet", Speaker], ["gear-import", "Upload gear", Upload]]],
  ["Website", [["home", "Home sections", LayoutGrid], ["branding", "Logo, favicon & social", Image], ["menu", "Menu", Menu], ["shipping", "Shipping", Truck], ["content", "Website content", FileText], ["blog", "Blog", PenSquare]]],
];

function Sidebar({ onNav }) {
  const LOGO = useLogo();
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    window.location.href = "/admin/login";
  };
  return (
    <aside className="w-64 bg-[#0d0d0e] text-zinc-300 flex flex-col h-full">
      <div className="p-5 border-b border-zinc-800">
        <div className="bg-white p-2 inline-block"><img src={LOGO} alt="Vinyl Record Vault" className="h-7" /></div>
        <p className="label-caps text-[10px] text-zinc-500 mt-3">Back office</p>
      </div>
      <nav className="flex-1 overflow-y-auto py-4">
        {NAV.map(([group, items]) => (
          <div key={group} className="mb-5">
            <p className="px-5 label-caps text-[10px] text-red mb-2">{group}</p>
            {items.map(([to, label, Icon]) => (
              <NavLink key={to} to={`/admin/${to}`} onClick={onNav} data-testid={`admin-nav-${to}`}
                className={({ isActive }) => `flex items-center gap-3 px-5 py-2 text-sm transition-colors ${isActive ? "bg-zinc-800 text-white border-l-2 border-[#dc1f26]" : "hover:text-white border-l-2 border-transparent"}`}>
                <Icon className="w-4 h-4" strokeWidth={1.6} /> {label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="p-4 border-t border-zinc-800 space-y-1">
        <Link to="/" target="_blank" className="flex items-center gap-3 px-1 py-2 text-sm hover:text-white" data-testid="admin-view-site"><ExternalLink className="w-4 h-4" /> View website</Link>
        <button onClick={logout} className="flex items-center gap-3 px-1 py-2 text-sm hover:text-white" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Sign out</button>
      </div>
    </aside>
  );
}

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  if (!localStorage.getItem(TOKEN_KEY)) return <Navigate to="/admin/login" replace />;
  return (
    <div className="min-h-screen bg-[#f4f4f2] flex">
      <div className="hidden lg:block sticky top-0 h-screen"><Sidebar /></div>
      {open && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <Sidebar onNav={() => setOpen(false)} />
          <button className="flex-1 bg-black/50" onClick={() => setOpen(false)} aria-label="Close menu"><X className="w-6 h-6 text-white ml-4" /></button>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="lg:hidden bg-[#0d0d0e] p-3 flex items-center gap-3">
          <button onClick={() => setOpen(true)} className="text-white" data-testid="admin-mobile-menu"><Menu className="w-6 h-6" /></button>
          <span className="text-white font-head font-bold">Back office</span>
        </div>
        <main className="p-5 sm:p-8 lg:p-10 max-w-[1300px]"><Outlet /></main>
      </div>
    </div>
  );
}
